import { z } from "zod"
import { utcDay } from "./dashboard-period"
import { marketplaceIdSchema } from "./marketplace-id"
import { err, ok, type ApiError, type Result } from "./result"
import { isoCalendarDay } from "./schemas"

export const PAYOUT_STATES = ["pending", "paid", "failed"] as const
export const payoutStateSchema = z.enum(PAYOUT_STATES)
export type PayoutState = z.infer<typeof payoutStateSchema>

export const PAYOUT_AVAILABILITIES = [
  "available",
  "partial",
  "not_synced",
  "gated",
  "unsupported",
] as const
export const payoutAvailabilitySchema = z.enum(PAYOUT_AVAILABILITIES)
export type PayoutAvailability = z.infer<typeof payoutAvailabilitySchema>

/** Whether a marketplace has a usable payout source at all, as the READ path has
 *  to know it. Two different absences, and collapsing them is what this type
 *  exists to prevent:
 *
 *  - `unsupported` — the platform publishes no payout resource (Octopia today);
 *  - `gated` — the platform does, but this marketplace rides shared credentials
 *    with no explicit shop id, so no source is ever constructed for it. Before
 *    this was visible to a read, such a marketplace answered `not_synced` and
 *    offered a Sync whose only possible outcome was an error — invisible while one
 *    dashboard showed one marketplace, unmissable on a page showing all of them.
 *
 *  `unconfigured` credentials (an unset or rotated-away key) deliberately do NOT
 *  land here: the mirror it already wrote is still true, and staleness there is
 *  the SWEEP's alarm to raise, not a reason for a read to disown its own rows. */
export type PayoutSourceState = "ready" | "gated" | "unsupported"

/** How many payout rows one history read returns. Totals still cover every row
 *  in the range; the response flags `truncated` when the list omits older
 *  cycles. Owned here so the SQL limit and the truncation test can't drift. */
export const PAYOUT_LIST_LIMIT = 1000

/** Inclusive window cap. Keeps one external read — and one mirror read —
 *  bounded to a year (plus the leap day). */
export const PAYOUT_RANGE_MAX_DAYS = 366

/**
 * How far back the payout mirror is kept, in whole calendar months including the
 * current one — the horizon {@link payoutSyncWindow} refreshes every run.
 *
 * A horizon rather than a trailing day count because settlement is a monthly
 * rhythm (the fleet pays on the 10th, 20th and 31st), so "how much history do we
 * hold" is only a meaningful promise in months. It is also what the page's
 * expanded cycle list can honestly claim to be complete back to — the list says
 * which day that is, because beyond it Nova holds nothing and must not imply
 * otherwise.
 *
 * Twelve is affordable because widening is very nearly free: SBC11 is one
 * seek-paginated `limit=1000` read whether it is asked for a month or a year, and
 * a year holds ~36 cycles per marketplace (three a month), so the daily
 * range-replace rewrites a few hundred rows across the fleet.
 */
export const PAYOUT_MONTH_HISTORY = 12

/** How many cycles a marketplace's block shows before it is expanded. Three is
 *  one month of settlement for this fleet, so the collapsed block answers "did the
 *  last payouts arrive" without anyone clicking. */
export const PAYOUT_PREVIEW_CYCLES = 3

/**
 * The window one payout sync covers when no explicit range is given: from the
 * first day of the oldest month still inside {@link PAYOUT_MONTH_HISTORY} through
 * today.
 *
 * Anchored to a month boundary rather than counted back in days, so the mirror's
 * coverage always starts on a first-of-month and the page can name that day
 * exactly. Today is included because a settlement cycle is a discrete event, not
 * an accruing metric, and the range is replaced whole on every run — a cycle
 * appearing later today simply lands in the next run's window.
 *
 * At twelve months this spans at most 366 days — exactly
 * {@link PAYOUT_RANGE_MAX_DAYS}, reached on 31 December of a leap year. The fit is
 * that tight on purpose and `payouts.test.ts` pins it; widening the history
 * without raising the cap would start failing the range gate.
 */
export function payoutSyncWindow(now: Date): PayoutRange {
  const oldestMonthStart = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth() - (PAYOUT_MONTH_HISTORY - 1),
    1
  )
  return { from: utcDay(oldestMonthStart), to: utcDay(now.getTime()) }
}

/** Inclusive payout window. Capped server-side to keep external reads bounded. */
export const payoutRangeSchema = z
  .object({
    from: isoCalendarDay,
    to: isoCalendarDay,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.from > value.to) {
      ctx.addIssue({ code: "custom", message: "from must be on or before to" })
    }
    const days =
      (Date.parse(`${value.to}T00:00:00.000Z`) - Date.parse(`${value.from}T00:00:00.000Z`)) /
        86_400_000 +
      1
    if (days > PAYOUT_RANGE_MAX_DAYS) {
      ctx.addIssue({
        code: "custom",
        message: `payout range cannot exceed ${PAYOUT_RANGE_MAX_DAYS} days`,
      })
    }
  })
export type PayoutRange = z.infer<typeof payoutRangeSchema>

/** The one range gate for both entry points (query string and JSON body), so a
 *  bad window reads the same whichever surface produced it. */
export function parsePayoutRange(input: {
  from: string | undefined
  to: string | undefined
}): Result<PayoutRange, ApiError> {
  const parsed = payoutRangeSchema.safeParse(input)
  if (!parsed.success) {
    return err({
      kind: "validation",
      message: "payout query requires a valid from/to date range",
      details: parsed.error.issues,
    })
  }
  return ok(parsed.data)
}

/** Exact amounts travel as decimal strings end-to-end — never through a float. */
export const decimalStringSchema = z
  .string()
  .regex(/^-?\d+(?:\.\d+)?$/, "expected an exact decimal string")

/** One payout cycle as every adapter must hand it over and the mirror stores
 *  it. Vendor-neutral on purpose: a marketplace adapter maps its own states and
 *  field names into this shape, and nothing downstream knows which platform it
 *  came from. */
export type PayoutRecord = {
  remoteId: string
  createdAt: string
  transferredAt: string | null
  state: PayoutState
  currency: string
  /** Decimal strings preserve the exact amount returned by the marketplace. */
  sellerAmount: string
  operatorAmount: string
  paymentVoucherReference: string | null
}

export const payoutSchema = z.object({
  payoutId: z.string(),
  createdAt: z.string(),
  transferredAt: z.string().nullable(),
  state: payoutStateSchema,
  currency: z.string(),
  sellerAmount: decimalStringSchema,
  operatorAmount: decimalStringSchema,
  paymentVoucherReference: z.string().nullable(),
})
export type Payout = z.infer<typeof payoutSchema>

export const payoutTotalsSchema = z.object({
  currency: z.string(),
  paid: decimalStringSchema,
  pending: decimalStringSchema,
  failed: decimalStringSchema,
})
export type PayoutTotals = z.infer<typeof payoutTotalsSchema>

export const payoutHistoryResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  from: isoCalendarDay,
  to: isoCalendarDay,
  availability: payoutAvailabilitySchema,
  lastSyncedAt: z.string().nullable(),
  /** True when the bounded list omits older cycles; totals still cover all rows. */
  truncated: z.boolean(),
  totals: z.array(payoutTotalsSchema),
  payouts: z.array(payoutSchema),
})
export type PayoutHistoryResponse = z.infer<typeof payoutHistoryResponseSchema>

/**
 * One marketplace's whole mirrored cycle history — the fleet page's per-block
 * payload. Takes no range at all: the page shows the newest
 * {@link PAYOUT_PREVIEW_CYCLES} and expands to everything on demand, so a window
 * would only be a question nobody asked.
 *
 * `coveredFrom` is what makes "all payouts" honest. It is the earliest day a
 * completed sync covers, so the expanded list can say where its own completeness
 * ends instead of implying it holds every payout ever made — the lifetime figure
 * is the account balance's `paid`, and that comes from the marketplace, not from
 * counting these rows. `null` when nothing has been synced.
 *
 * `cycleCount` is the count in the mirror; `payouts` carries them all, bounded per
 * marketplace by {@link PAYOUT_LIST_LIMIT}, with `truncated` saying when older
 * cycles were left out. The list is sent whole rather than paged because expanding
 * must be instant and a year is ~36 rows — the payload is a dozen rows over what
 * the collapsed block renders, against a request per click.
 */
export const payoutFleetEntrySchema = z.object({
  marketplace: marketplaceIdSchema,
  availability: payoutAvailabilitySchema,
  lastSyncedAt: z.string().nullable(),
  coveredFrom: isoCalendarDay.nullable(),
  cycleCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
  totals: z.array(payoutTotalsSchema),
  payouts: z.array(payoutSchema),
})
export type PayoutFleetEntry = z.infer<typeof payoutFleetEntrySchema>

/** Every CONFIGURED marketplace's payout history in one read — `gated` and
 *  `unsupported` included, because the page shows those as named collapsed rows
 *  and a marketplace omitted from the fleet is an absence with no reason. */
export const payoutFleetResponseSchema = z.object({
  marketplaces: z.array(payoutFleetEntrySchema),
})
export type PayoutFleetResponse = z.infer<typeof payoutFleetResponseSchema>

/**
 * The marketplace account's money position, as every adapter must hand it over.
 * A SNAPSHOT, not an event: it has no remote id, no state of its own and no
 * transfer date, and it moves while nothing happens — which is why it lives in
 * its own table rather than among the cycles (`docs/DECISIONS.md` → Payouts).
 *
 * The three figures keep the marketplace's own vocabulary so the dashboard can
 * be laid beside the seller portal and reconciled one-to-one:
 *
 * - `pending` — earned, not yet due;
 * - `payable` — due, waiting for the next payout to carry it;
 * - `paid` — cumulative since the shop opened, NOT the period on screen. This is
 *   why the tile keeps the word "lifetime": it is routinely many times any single
 *   cycle listed beneath it, and unlabelled it reads as a defect.
 */
export type PayoutBalanceRecord = {
  currency: string
  /** Decimal strings preserve the exact amount the marketplace reported. */
  pending: string
  payable: string
  paid: string
  /** The shop the marketplace says it answered for, kept as EVIDENCE that the
   *  read was scoped to our shop. Never an identity: a marketplace config carries
   *  at most one shop, so `marketplace` alone keys the row. */
  shopReference: string | null
}

export const payoutBalanceSchema = z.object({
  currency: z.string(),
  pending: decimalStringSchema,
  payable: decimalStringSchema,
  paid: decimalStringSchema,
  shopReference: z.string().nullable(),
  /** Calendar day the snapshot was captured on — the row's identity with
   *  `marketplace`, so one day holds exactly one snapshot. */
  capturedOn: isoCalendarDay,
  capturedAt: z.string(),
})
export type PayoutBalance = z.infer<typeof payoutBalanceSchema>

/** The latest snapshot for one marketplace. `null` is "nothing captured yet" and
 *  must never render as three zeros — zero is a claim about money, an absent
 *  snapshot is the absence of one. `supported` is false where the platform
 *  publishes no payout source at all. */
export const payoutBalanceResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  supported: z.boolean(),
  balance: payoutBalanceSchema.nullable(),
})
export type PayoutBalanceResponse = z.infer<typeof payoutBalanceResponseSchema>

/** Every marketplace's account position in one read. Range-free, exactly like the
 *  single-marketplace balance: an as-of figure has no period, so the page's month
 *  selector must not appear to move it — and does not refetch it. */
export const payoutBalancesResponseSchema = z.object({
  balances: z.array(payoutBalanceResponseSchema),
})
export type PayoutBalancesResponse = z.infer<typeof payoutBalancesResponseSchema>

export const payoutSyncResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  from: isoCalendarDay,
  to: isoCalendarDay,
  syncedAt: z.string(),
  payoutCount: z.number().int().nonnegative(),
})
export type PayoutSyncResponse = z.infer<typeof payoutSyncResponseSchema>

/** What the sweep did for one marketplace. `skipped` is a DELIBERATE
 *  configuration fact the sweep reports and moves past — a marketplace on shared
 *  credentials with no explicit shop id — never a failure. `failed` is everything
 *  that leaves the mirror unrefreshed without anyone having chosen that: the
 *  marketplace answering badly, the write failing, credentials that were expected
 *  and are gone, or a marketplace the sweep's budget never reached. That is what
 *  the caller alerts on. */
export const payoutSyncOutcomeSchema = z.discriminatedUnion("status", [
  z.object({
    marketplace: marketplaceIdSchema,
    status: z.literal("synced"),
    payoutCount: z.number().int().nonnegative(),
  }),
  z.object({
    marketplace: marketplaceIdSchema,
    status: z.literal("skipped"),
    reason: z.string(),
  }),
  z.object({
    marketplace: marketplaceIdSchema,
    status: z.literal("failed"),
    error: z.string(),
  }),
])
export type PayoutSyncOutcome = z.infer<typeof payoutSyncOutcomeSchema>

/** One sweep over every payout-capable marketplace for one window. `totals` are
 *  summed in Postgres over exactly the cycles the sweep wrote, per currency, so
 *  exact amounts never pass through a float. */
export const payoutSweepResponseSchema = z.object({
  from: isoCalendarDay,
  to: isoCalendarDay,
  syncedAt: z.string(),
  results: z.array(payoutSyncOutcomeSchema),
  totals: z.array(payoutTotalsSchema),
})
export type PayoutSweepResponse = z.infer<typeof payoutSweepResponseSchema>

/** How much of a requested window the mirror can actually answer for. Pure over
 *  the coverage inputs so the API and the dashboard read the same rule. The two
 *  source absences are answered before any coverage question: neither a gated nor
 *  an unsupported marketplace can be synced, so "how much is covered" is not a
 *  question they have. */
export function payoutAvailability(input: {
  source: PayoutSourceState
  fullyCovered: boolean
  overlaps: boolean
  hasPayouts: boolean
}): PayoutAvailability {
  if (input.source !== "ready") {
    return input.source
  }
  if (input.fullyCovered) {
    return "available"
  }
  if (input.overlaps || input.hasPayouts) {
    return "partial"
  }
  return "not_synced"
}

/** The same question for a read with NO range — the fleet page's. `partial` is
 *  deliberately unreachable here: partial is a statement about a window, and a
 *  read that asked for no window cannot make it. What is left is the honest pair —
 *  something has been synced, or nothing has — plus the two source absences, which
 *  are answered first for the same reason as above. */
export function payoutMirrorAvailability(input: {
  source: PayoutSourceState
  synced: boolean
}): PayoutAvailability {
  if (input.source !== "ready") {
    return input.source
  }
  return input.synced ? "available" : "not_synced"
}
